import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import styled, { withTheme } from 'styled-components';
import throttle from 'lodash/throttle';
import isNull from 'lodash/isNull';
import { hook } from '@g4/automation-hooks';
import { TrackClick } from '@g4/trackster';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import IsStadium from '../../utils/isStadium';

import { Layout, useMedia, Box, ConditionalWrap } from '../Boneyard';
import BurgerMenu from './siteHeader/BurgerMenu';
import Logo from './siteHeader/Logo';
import NavGroup from './siteHeader/NavGroup';
import UserMenu from './siteHeader/UserMenu';
import SkipToContent from './siteHeader/SkipToContent';
import MerchandiseBanner from './siteHeader/MerchandiseBanner';

import { useAuthentication, useSSRQuery } from '../../graphql';

import SITE_HEADER_QUERY from './siteHeader/siteHeader.query.graphql';
import SITE_HEADER_TRANSLATONS_QUERY from './siteHeader/siteHeaderTranslations.query.graphql.js';
import MERCHANDISE_BANNER_QUERY from './siteHeader/merchandiseBanner.query.graphql';

import {
  HEADER_LOGO,
  HEADER_TOP_BAR_MENU,
  HEADER_MAIN_MENU,
  HEADER_CART_BUTTON,
} from '../../../automationHooks.json';

import CART_ICON_DARK from '../../images/ic-cart-dark.svg';
import CART_ICON_LIGHT from '../../images/ic-cart-light.svg';

import { white, black } from '../../theme/theme/colors';
import AllegiantStadiumBanner from './siteHeader/AllegiantStadiumBanner';
import { AllegiantStadiumBannerContext } from '../../pageComponents/app/AllegiantStadiumBannerProvider';
import { AgentBanner } from '@g4/next-furniture/AgentBanner';
import { useModal } from '../ModalProvider';
import useIdleTimeout from '@g4/next-furniture/LoginSignup/hooks/useIdleTimeout';

const propTypes = {
  theme: PropTypes.shape({
    zIndex: PropTypes.shape({
      siteHeader: PropTypes.number.isRequired,
    }),
  }),
  hideNavOptions: PropTypes.bool,
  hideStadiumBanner: PropTypes.bool,
};
const defaultProps = {
  hideNavOptions: false,
  hideStadiumBanner: false,
};

const TOP_BAR_HEIGHT = 44;
const HEADER_HEIGHT_SMALL = 50;
const HEADER_HEIGHT_LARGE = 86;
const STADIUM_BANNER_HEIGHT = 44;

const Header = styled(Layout.Header)`
  ${props => props.theme.query.lg} {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
  }
`;

const DrawerDivider = styled.hr`
  border-top: 2px solid ${props => props.theme.colors.siteHeader.linkActive};
`;

const StyledNav = styled.nav`
  box-shadow: 0 1px 5px 0 ${props => props.theme.colors.siteHeader.boxShadow};
  position: fixed;
  z-index: 1;
  width: 100%;
`;

const TopBar = styled.div`
  height: ${TOP_BAR_HEIGHT}px;
  width: 100%;
  position: relative;
  z-index: 1;
  background-image: linear-gradient(
    ${props => props.theme.colors.siteHeader.topBarBackground.from},
    ${props => props.theme.colors.siteHeader.topBarBackground.to}
  );
  li {
    font-size: 14px;
  }
`;

const BelowHeaderBanner = styled.div`
  height: ${TOP_BAR_HEIGHT}px;
  width: 100%;
  position: relative;
  ${props =>
    props.bgColor
      ? `background-color:${props.bgColor}`
      : `background-image:linear-gradient(
    ${props.theme.colors.siteHeader.topBarBackground.from},
    ${props.theme.colors.siteHeader.topBarBackground.to}
  )`};
  li {
    font-size: 14px;
  }
`;

const Constrain = styled.div`
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 calc(${props => props.theme.spacing(6)} / 2);
  display: flex;
  justify-content: flex-end;
`;

const CartButtonContainer = styled.div`
  height: ${props => props.buttonHeight}px;
  display: flex;
  align-items: flex-end;
`;

const CartButton = styled.button.attrs({
  type: 'button',
})`
  height: 20px;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const CartIcon = styled.img.attrs(props => ({
  src: `${props.isStadium ? CART_ICON_LIGHT : CART_ICON_DARK}`,
  alt: 'Cart Icon',
}))`
  height: 20px;
  width: 20px;
`;

const SiteHeader = ({ theme, hideNavOptions, hideStadiumBanner }) => {
  const {
    setShowModal,
    setContent,
    setTitle,
    isModalAutoClosed,
    setIsModalAutoClosed,
    setIsMobile,
  } = useModal();
  const { stadiumBannerData } = useContext(AllegiantStadiumBannerContext) || {};
  const {
    loadingAllegiantStadiumBanner,
    allegiantStadiumBannerError,
    allegiantStadiumBannerData,
  } = stadiumBannerData || {};

  const { isAuthenticated, isAnonymousUser } = useAuthentication();
  const mq = useMedia();
  const [hasScrolled, setHasScrolled] = React.useState(false);

  const { agentType } = useUserContext();
  const router = useRouter();
  const isTravelAgentLoginPage = router.pathname === '/ta-login';

  const displayLoginButton =
    (isNull(agentType) || !isAnonymousUser) && !isTravelAgentLoginPage;

  let maxHeaderHeightLg = HEADER_HEIGHT_LARGE + TOP_BAR_HEIGHT;
  const maxHeaderHeight = HEADER_HEIGHT_SMALL;

  React.useEffect(() => {
    const onScroll = throttle(() => {
      setHasScrolled(
        document.body.scrollTop > maxHeaderHeightLg ||
          document.documentElement.scrollTop > maxHeaderHeightLg
      );
    });

    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [maxHeaderHeightLg]);

  const headerHeightMobile = HEADER_HEIGHT_SMALL;
  const headerHeightDesktop = hasScrolled
    ? HEADER_HEIGHT_SMALL
    : HEADER_HEIGHT_LARGE;

  const { loading, error, data } = useSSRQuery(SITE_HEADER_QUERY);
  const { data: translationsData } = useSSRQuery(SITE_HEADER_TRANSLATONS_QUERY);

  const {
    loading: loadingMerchandiseBanner,
    error: merchandiseBannerError,
    data: merchandiseBannerData,
    refetch,
  } = useSSRQuery(MERCHANDISE_BANNER_QUERY, {
    cachePolicy: 'cache-and-network',
    ssr: false,
  });

  useIdleTimeout(
    setShowModal,
    setContent,
    setTitle,
    translationsData?.application?.translations,
    isModalAutoClosed,
    setIsModalAutoClosed,
    setIsMobile
  );

  React.useEffect(() => {
    refetch && refetch();
  }, [isAuthenticated, refetch]);

  if (loading || loadingMerchandiseBanner || loadingAllegiantStadiumBanner) {
    return null;
  }

  if (error || merchandiseBannerError || allegiantStadiumBannerError) {
    throw error;
  }

  const renderCart = (
    <CartButtonContainer>
      <TrackClick
        category="site_click_tracking"
        action="Top Nav"
        label="View Cart"
      >
        <CartButton {...hook(HEADER_CART_BUTTON.hook)}>
          <CartIcon isStadium={IsStadium()} />
        </CartButton>
      </TrackClick>
    </CartButtonContainer>
  );

  const { mainMenu, topMenu } = data?.application;
  const merchandiseBanner =
    merchandiseBannerData?.application?.merchandiseBanner;

  const allegiantStadiumBanner =
    allegiantStadiumBannerData?.application?.merchandiseBanner;

  if (allegiantStadiumBanner && !hideStadiumBanner) {
    maxHeaderHeightLg += STADIUM_BANNER_HEIGHT;
  }

  if (mq && mq.gte('md')) {
    return (
      <div
        style={{
          height: maxHeaderHeightLg,
          zIndex: theme.zIndex.siteHeader,
          background: IsStadium() ? `${black}` : `${white}`,
        }}
      >
        <SkipToContent>Skip to content</SkipToContent>

        <Header as="header">
          <StyledNav>
            <AgentBanner />
            <TopBar>
              <Constrain>
                {merchandiseBanner && (
                  <MerchandiseBanner banner={merchandiseBanner} />
                )}
                {!hideNavOptions && (
                  <NavGroup
                    items={topMenu}
                    defaultBackgroundType="dark"
                    automationHook={HEADER_TOP_BAR_MENU.hook}
                  />
                )}
              </Constrain>
            </TopBar>
            <Layout.Strip color={IsStadium() ? `${black}` : `${white}`}>
              <Logo
                logoHeight={headerHeightDesktop}
                automationHook={HEADER_LOGO.hook}
              />

              <Box
                d="flex"
                alignItems="flex-end"
                w="100%"
                justifyContent="space-between"
                height={headerHeightDesktop}
              >
                <NavGroup
                  items={mainMenu}
                  automationHook={HEADER_MAIN_MENU.hook}
                  isAllegiantStadiumNavItems={IsStadium() ? true : false}
                  displayAsColumn={mq.lt('md')}
                />
                {displayLoginButton && (
                  <Box d="flex">
                    <UserMenu />
                    {renderCart}
                  </Box>
                )}
              </Box>
            </Layout.Strip>
            {allegiantStadiumBanner && !hideStadiumBanner && (
              <BelowHeaderBanner bgColor={allegiantStadiumBanner?.color}>
                <Constrain>
                  <AllegiantStadiumBanner banner={allegiantStadiumBanner} />
                </Constrain>
              </BelowHeaderBanner>
            )}
          </StyledNav>
        </Header>
      </div>
    );
  }

  return (
    <ConditionalWrap
      condition={!!merchandiseBanner}
      wrap={children => (
        <div
          style={{
            zIndex: theme.zIndex.siteHeader,
          }}
        >
          {children}
          <Box d="flex" direction="colomn">
            {allegiantStadiumBanner && (
              <AllegiantStadiumBanner banner={allegiantStadiumBanner} />
            )}
            {merchandiseBanner && (
              <MerchandiseBanner banner={merchandiseBanner} />
            )}
          </Box>
        </div>
      )}
    >
      <div
        style={{
          height: maxHeaderHeight,
          zIndex: theme.zIndex.siteHeader,
        }}
      >
        <SkipToContent>Skip to content</SkipToContent>

        <Header as="header">
          <StyledNav>
            <AgentBanner />
            <Layout.Strip color={IsStadium() ? `${black}` : `${white}`}>
              <Logo
                logoHeight={headerHeightMobile}
                automationHook={HEADER_LOGO.hook}
              />
              <Box d="flex">
                {renderCart}

                <BurgerMenu
                  headerHeight={headerHeightMobile}
                  isAuthenticated={isAuthenticated}
                  isStadium={IsStadium()}
                >
                  <UserMenu isBurgerMenu />
                  <DrawerDivider />
                  <NavGroup
                    items={topMenu}
                    automationHook={HEADER_TOP_BAR_MENU.hook}
                    displayAsColumn
                  />
                  <DrawerDivider />
                  <NavGroup
                    items={mainMenu}
                    automationHook={HEADER_MAIN_MENU.hook}
                    isAllegiantStadiumNavItems={IsStadium() ? true : false}
                    displayAsColumn
                  />
                </BurgerMenu>
              </Box>
            </Layout.Strip>
          </StyledNav>
        </Header>
      </div>
    </ConditionalWrap>
  );
};

SiteHeader.propTypes = propTypes;
SiteHeader.defaultProps = defaultProps;

export default withTheme(SiteHeader);
